import { createAsyncThunk } from '@reduxjs/toolkit';
import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import {
  AgencyByIdsParams,
  AgencyByIdsResponse,
  GetAgencyDataResponse,
  GetAgencyListParams,
  GetAgencyDocumentListResponse,
  GetAgencyDocumentListParams,
  GetAgencyTimesheetListParams,
  GetAgencyTimesheetListResponse,
  GetAgencyBranchesListResponse,
  GetAgencyBranchesListParams,
  GetAgencyContactsListResponse,
  GetAgencyContactsListParams,
} from '../types';
import {
  GetAssignmentListParams,
  GetAssignmentListResponse,
} from '../../assignment/types';
import {
  GetEmployeeListParams,
  GetEmployeeListResponse,
} from '../../employee/types';

export const getAgencyList = createAsyncThunk<
  GetAgencyDataResponse,
  GetAgencyListParams
>(
  'agency/getAgencyList',
  async (params: GetAgencyListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.agency.main, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const agencyByIds = createAsyncThunk<
  AgencyByIdsResponse,
  AgencyByIdsParams
>(
  'agency/getAgencyByIds',
  async (params: AgencyByIdsParams, { rejectWithValue }) => {
    try {
      const { ids } = params;
      const response = await jwtAxios.put(endPoints.agency.main, ids);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAgencyDocumentListById = createAsyncThunk<
  GetAgencyDocumentListResponse,
  { id: string; params: GetAgencyDocumentListParams }
>(
  'agency/getAgencyDocumentList',
  async (
    args: { id: string; params: GetAgencyDocumentListParams },
    { rejectWithValue },
  ) => {
    try {
      const { id, params } = args;
      const response = await jwtAxios.get(
        `${endPoints.agency.main}/document/${id}`,
        {
          params,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAgencyTimesheetListById = createAsyncThunk<
  GetAgencyTimesheetListResponse,
  { id: string; params: GetAgencyTimesheetListParams }
>(
  'agency/getAgencyTimesheetListById',
  async (
    args: { id: string; params: GetAgencyTimesheetListParams },
    { rejectWithValue },
  ) => {
    try {
      const { id, params } = args;
      const response = await jwtAxios.get(
        `${endPoints.agency.main}/time-sheets/${id}`,
        {
          params,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAgencyBranchesById = createAsyncThunk<
  GetAgencyBranchesListResponse,
  { id: string; params: GetAgencyBranchesListParams }
>(
  'agency/getAgencyBranchesById',
  async (
    args: { id: string; params: GetAgencyBranchesListParams },
    { rejectWithValue },
  ) => {
    try {
      const { id, params } = args;
      const response = await jwtAxios.get(
        `${endPoints.agency.main}/site/${id}`,
        {
          params,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAgencyContactsById = createAsyncThunk<
  GetAgencyContactsListResponse,
  { id: string; params: GetAgencyContactsListParams }
>(
  'agency/getAgencyContactsById',
  async (
    args: { id: string; params: GetAgencyBranchesListParams },
    { rejectWithValue },
  ) => {
    try {
      const { id, params } = args;
      const response = await jwtAxios.get(
        `${endPoints.agency.main}/contact/${id}`,
        {
          params,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAgencyAssignmentList = createAsyncThunk<
  GetAssignmentListResponse,
  { id: string; params: GetAssignmentListParams }
>(
  'agency/getAgencyAssignmentList',
  async (
    args: { id: string; params: GetAssignmentListParams },
    { rejectWithValue },
  ) => {
    try {
      const { id, params } = args;
      const response = await jwtAxios.get(
        `${endPoints.agency.main}/assignments/${id}`,
        {
          params,
        },
      );

      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAgencyEmployeeList = createAsyncThunk<
  GetEmployeeListResponse,
  GetEmployeeListParams & { id: string }
>('agency/getEmployeeList', async ({ id, ...params }, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(
      `${endPoints.agency.main}/subcontractors/${id}`,
      {
        params,
      },
    );
    return {
      data: response.data.data,
      pagination: {
        current: params.page || 0,
        pageSize: params.size || 20,
        totalDocs: response.data.totalDocs,
      },
      queryParams: params,
    };
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const agencyActs = {
  getAgencyList,
  agencyByIds,
  getAgencyDocumentListById,
  getAgencyTimesheetListById,
  getAgencyBranchesById,
  getAgencyContactsById,
  getAgencyAssignmentList,
  getAgencyEmployeeList,
};
